import { Component, EventEmitter, Output, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Button } from '../button/button';
import { GuardrailConfigService, GuardrailTestResult } from '../../../core/services/guardrail-config';

@Component({
  selector: 'app-guardrail-test-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, Button],
  templateUrl: './guardrail-test-modal.html',
  styleUrl: './guardrail-test-modal.css'
})
export class GuardrailTestModal {
  @Output() closed = new EventEmitter<void>();

  private guardrailService = inject(GuardrailConfigService);
  private sanitizer = inject(DomSanitizer);

  view = signal<'prompt' | 'results'>('prompt');
  prompt = signal('');
  isRunning = signal(false);
  result = signal<GuardrailTestResult | null>(null);

  enabledGuardrailNames = computed(() =>
    this.guardrailService.guardrails().filter(g => g.enabled).map(g => g.name)
  );

  runTest() {
    const value = this.prompt().trim();
    if (!value || this.isRunning()) return;

    this.isRunning.set(true);
    this.guardrailService.runTest(value).subscribe({
      next: res => {
        this.isRunning.set(false);
        this.result.set(res);
        this.view.set('results');
      },
      error: () => {
        this.isRunning.set(false);
        this.result.set({
          notHit: false,
          withoutGuardrails: 'Request to the guardrails API failed.',
          withGuardrails: 'Request to the guardrails API failed.',
          blocked: true,
          blockedStage: null,
          triggeredGuardrails: [],
          error: true
        });
        this.view.set('results');
      }
    });
  }

  formatMarkdown(text: string): SafeHtml {
    if (!text) return this.sanitizer.bypassSecurityTrustHtml('');
    return this.sanitizer.bypassSecurityTrustHtml(this.parseMarkdown(text));
  }

  private parseMarkdown(raw: string): string {
    const escape = (s: string) =>
      s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

    const inline = (s: string): string =>
      escape(s)
        .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/`([^`]+)`/g, '<code>$1</code>');

    const blocks: string[] = [];
    const lines = raw.split('\n');
    let i = 0;

    while (i < lines.length) {
      const line = lines[i];

      if (line.startsWith('```')) {
        const codeLines: string[] = [];
        i++;
        while (i < lines.length && !lines[i].startsWith('```')) {
          codeLines.push(escape(lines[i]));
          i++;
        }
        blocks.push(`<pre><code>${codeLines.join('\n')}</code></pre>`);
        i++;
        continue;
      }

      const h3 = line.match(/^### (.+)/);
      if (h3) { blocks.push(`<h5>${inline(h3[1])}</h5>`); i++; continue; }
      const h2 = line.match(/^## (.+)/);
      if (h2) { blocks.push(`<h4>${inline(h2[1])}</h4>`); i++; continue; }
      const h1 = line.match(/^# (.+)/);
      if (h1) { blocks.push(`<h3>${inline(h1[1])}</h3>`); i++; continue; }

      if (line.trim() === '---') { blocks.push('<hr>'); i++; continue; }

      if (line.trim().startsWith('|')) {
        const tableLines: string[] = [];
        while (i < lines.length && lines[i].trim().startsWith('|')) {
          tableLines.push(lines[i]);
          i++;
        }
        const nonSep = tableLines.filter(l => !/^\|[\s|:-]+\|$/.test(l.trim()));
        if (nonSep.length > 0) {
          const cells = (row: string) =>
            row.split('|').filter((_, idx, arr) => idx > 0 && idx < arr.length - 1).map(c => c.trim());
          let table = '<table class="md-table"><thead><tr>';
          cells(nonSep[0]).forEach(c => { table += `<th>${inline(c)}</th>`; });
          table += '</tr></thead><tbody>';
          nonSep.slice(1).forEach(row => {
            table += '<tr>';
            cells(row).forEach(c => { table += `<td>${inline(c)}</td>`; });
            table += '</tr>';
          });
          table += '</tbody></table>';
          blocks.push(table);
        }
        continue;
      }

      if (/^[*-] /.test(line)) {
        const items: string[] = [];
        while (i < lines.length && /^[*-] /.test(lines[i])) {
          items.push(`<li>${inline(lines[i].replace(/^[*-] /, ''))}</li>`);
          i++;
        }
        blocks.push(`<ul>${items.join('')}</ul>`);
        continue;
      }

      if (line.trim() === '') { i++; continue; }

      const paraLines: string[] = [];
      while (
        i < lines.length &&
        lines[i].trim() !== '' &&
        !lines[i].startsWith('#') &&
        !lines[i].startsWith('```') &&
        lines[i].trim() !== '---' &&
        !lines[i].trim().startsWith('|') &&
        !/^[*-] /.test(lines[i])
      ) {
        paraLines.push(inline(lines[i]));
        i++;
      }
      if (paraLines.length > 0) {
        blocks.push(`<p>${paraLines.join('<br>')}</p>`);
      }
    }

    return blocks.join('');
  }

  editPrompt() {
    this.view.set('prompt');
  }

  onOverlayClick() {
    this.closed.emit();
  }

  onClose() {
    this.closed.emit();
  }
}
